/**
 * The document view of the graph window (Word's, Writing's): the selected node's document,
 * written on sheets of paper (`Editor.tsx`), or when it has none the start screen: write the
 * selected node's document, make a new page from a template (Character, Place, Scene... and the
 * project's own), open one of the documents, or turn the old Markdown pages into documents.
 */
import "prosemirror-view/style/prosemirror.css";
import "prosemirror-tables/style/tables.css";
import "prosemirror-gapcursor/style/gapcursor.css";
import { For, Show, createEffect, createMemo, createSignal, on, type JSX } from "solid-js";
import type { Node } from "@protocol/Node";
import type { TemplateDef } from "@protocol/TemplateDef";
import { useApp } from "../../../app/context";
import { documentOf, templateFor } from "../../../state/documents";
import { toast } from "../../../shared/toast";
import { Editor } from "./Editor";

/** The document view. */
export function DocumentView(): JSX.Element {
  const { documents, pages, graph, access, view } = useApp();
  /** A document opened from the list (`null`: the selected node's). */
  const [picked, setPicked] = createSignal<string | null>(null);
  /** `true` while the start screen is asked for (the way back from a document). */
  const [browsing, setBrowsing] = createSignal(false);
  const [name, setName] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const canEdit = () => access.can("editor");

  // Selecting another node shows its document.
  createEffect(
    on(
      () => pages.current()?.id,
      () => {
        setPicked(null);
        setBrowsing(false);
      },
      { defer: true },
    ),
  );

  const nodeOf = (path: string): Node | null => graph.state.graph.nodes.find((n) => documentOf(n) === path) ?? null;
  /** The document shown (`null`: the start screen). */
  const shown = createMemo(() => {
    if (browsing()) return null;
    return picked() ?? (pages.current() ? documentOf(pages.current()!) : null);
  });
  const listed = createMemo(() => [...documents.index().values()].sort((a, b) => a.path.localeCompare(b.path)));

  /** Opens `path` (selecting its node, when it has one). */
  const open = (path: string) => {
    const node = nodeOf(path);
    if (node) pages.show(node.id);
    setPicked(path);
    setBrowsing(false);
  };

  /** Makes a page from `template` named `title` (for node `node`, when given), then opens it. */
  const create = async (template: TemplateDef, title: string, node?: Node) => {
    if (!title.trim()) return void toast("Give the new page a name first.", "error");
    setBusy(true);
    const made = await documents.create(template.id, title.trim(), node?.id);
    setBusy(false);
    if (!made) return;
    setName("");
    pages.show(made.node);
    setPicked(made.path);
    setBrowsing(false);
  };

  return (
    <div class="doc-view" id="doc-view">
      <Show when={shown()} keyed fallback={<Start />}>
        {(path) => (
          <Editor
            path={path}
            node={nodeOf(path)}
            back={() => {
              setPicked(null);
              setBrowsing(true);
            }}
          />
        )}
      </Show>
    </div>
  );

  /** The start screen. */
  function Start(): JSX.Element {
    const selected = () => pages.current();
    return (
      <div class="doc-start" id="doc-start">
        <Show when={documents.unconverted().length}>
          <div class="doc-banner" id="doc-convert-banner">
            <span>
              {documents.unconverted().length} page{documents.unconverted().length === 1 ? " is" : "s are"} still written the old way (Markdown).
            </span>
            <button
              type="button"
              class="btn primary"
              id="doc-convert"
              disabled={!canEdit() || busy()}
              onClick={async () => {
                setBusy(true);
                const count = await documents.convert();
                setBusy(false);
                if (count) toast(`${count} page${count === 1 ? "" : "s"} turned into documents. Press Ctrl+Z to undo.`);
              }}
            >
              Convert pages to documents
            </button>
          </div>
        </Show>
        <Show when={selected()}>
          {(node) => (
            <section class="doc-start-node">
              <h3>{node().name}</h3>
              <p class="muted">This node has no document yet.</p>
              <button
                type="button"
                class="btn primary"
                id="doc-write"
                disabled={!canEdit() || busy() || !documents.templates().length}
                onClick={() => {
                  const t = templateFor(documents.templates(), node().kind);
                  if (t) void create(t, node().name, node());
                }}
              >
                Write its document
              </button>
            </section>
          )}
        </Show>
        <section>
          <h3>New page</h3>
          <input id="doc-new-name" placeholder="Name (a character, a place, a scene…)" aria-label="New page's name" disabled={!canEdit()} value={name()} onInput={(e) => setName(e.currentTarget.value)} />
          <div class="doc-templates">
            <For each={documents.templates()} fallback={<p class="muted">No templates here: turn on an environment that brings some, such as Writing.</p>}>
              {(t) => (
                <button type="button" class="doc-template-card" data-template={t.id} data-template-source={t.source ?? "builtin"} disabled={!canEdit() || busy()} title={t.summary} onClick={() => void create(t, name())}>
                  <span class="doc-template-sheet" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </span>
                  <b>{t.name}</b>
                  <span class="muted">{t.summary}</span>
                </button>
              )}
            </For>
          </div>
        </section>
        <section>
          <h3>Documents</h3>
          <Show when={listed().length} fallback={<p class="muted">No document yet.</p>}>
            <ul class="doc-list">
              <For each={listed()}>
                {(p) => (
                  <li>
                    <button type="button" class="linklike" data-doc={p.path} onClick={() => open(p.path)}>
                      {p.title}
                    </button>
                    <span class="muted">
                      {" "}
                      {documents.template(p.template)?.name ?? ""} · {p.words} words
                    </span>
                  </li>
                )}
              </For>
            </ul>
          </Show>
          <Show when={view.views().includes("blueprint")}>
            <p class="muted">The blueprint (D) shows how the pages lead to each other.</p>
          </Show>
        </section>
      </div>
    );
  }
}
